import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl'
import Helmet from 'react-helmet'
import * as routeActions from 'services/routes/actions'
import * as routes from 'services/routes/selectors'
import * as site from 'services/site/selectors'
import * as userSelectors from 'services/user/selectors'
import { formValueSelector } from 'redux-form'
import { Row, Col } from 'react-flexbox-grid'
import RouteSearchForm from './form/RouteSearchForm'
import RouteSearchResults from './RouteSearchResults'
import { RouteMap } from './map/RouteMap'
import RouteSearchTitle from './RouteSearchTitle'
import styles from './_routeSearchPage.css'

export const FORM_NAME = 'routeSearch'
const formSelector = formValueSelector(FORM_NAME)

const messages = defineMessages({
  title: {
    id: 'routeseach.head.title',
    defaultMessage: '{siteSlug} training routes, exercise and training routes in {displayLocation}',
  },
  metaDescription: {
    id: 'routesearch.head.description',
    defaultMessage: `Find the best {siteSlug} training routes for exercise and fitness in {displayLocation}.
    Running, cycling, walking, hiking and triathlon maps for exercise training.`,
  },
  metaKeywords: {
    id: 'routesearch.head.keywords',
    defaultMessage: `{siteSlug} training routes, exercise and fitness, endurance training,
    exercise training, map your running route, fitness exercise, fitness log, {displayLocation}`,
  },
})

const mapStateToProps = (state) => ({
  googleMapsLoaded: routes.selectGoogleMapsLoaded(state),
  searchResults: routes.selectSearchResults(state),
  searchResultsCount: routes.selectSearchResultsCount(state),
  searchLoading: routes.selectSearchLoading(state),
  unitPreference: formSelector(state, 'unit'),
  selectedRouteId: routes.selectSelectedRouteId(state),
  noRoutesFound: routes.selectNoRoutesFound(state),
  searchCoords: routes.selectSearchCoords(state),
  mapMarkers: routes.selectMapMarkers(state),
  nextLink: routes.selectNextLink(state),
  prevLink: routes.selectPrevLink(state),
  siteSlug: site.selectSiteSlug(state),
  kmlLink: routes.selectKmlLink(state),
  displayLocation: userSelectors.selectDisplayLocation(state),
})

const mapDispatchToActions = { ...routeActions }

export class RouteSearchPage extends Component {

  static propTypes = {
    googleMapsLoaded: PropTypes.bool,
    verifyGoogleMapsScriptLoad: PropTypes.func,
    searchRoutes: PropTypes.func,
    getLocationSuggestions: PropTypes.func,
    searchLoading: PropTypes.bool,
    searchResults: PropTypes.array,
    searchResultsCount: PropTypes.number,
    unitPreference: PropTypes.string,
    selectRoute: PropTypes.func,
    selectedRouteId: PropTypes.string,
    noRoutesFound: PropTypes.bool,
    searchCoords: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    mapMarkers: PropTypes.array,
    getNextRoutes: PropTypes.func,
    getPrevRoutes: PropTypes.func,
    nextLink: PropTypes.string,
    prevLink: PropTypes.string,
    siteSlug: PropTypes.string,
    kmlLink: PropTypes.string,
    intl: PropTypes.object,
    displayLocation: PropTypes.string,
  }

  state = {
    geolocationError: false,
    geolocationLoading: false,
  }

  componentDidMount () {
    this.props.verifyGoogleMapsScriptLoad()
  }

  handleRouteSearchSubmit = (routeData) => {
    this.props.searchRoutes(routeData)
  }

  handleRouteSearch = () => {
    this.props.searchRoutes()
  }

  handleSelectRoute = (routeId) => {
    this.props.selectRoute(routeId)
  }

  handleGetLocationSuggestions = (location, setUserCoords) => {
    this.props.getLocationSuggestions(location, setUserCoords)
  }

  handleSortRoutesByDate = (showOldest) => {
    this.props.searchRoutes(null, showOldest)
  }

  handleMapLoad = (map) => {
    this.map = map
  }

  handleSearchMapLocation = () => {
    const center = this.map.getCenter()
    const formattedCenter = `${center.lat()},${center.lng()}`
    this.props.searchRoutes(null, false, formattedCenter)
  }

  handleShowMyLocation = () => {
    this.setState({
      geolocationLoading: true,
    })

    window.navigator.geolocation.getCurrentPosition((pos) => {
      const formattedCoords = `${pos.coords.latitude},${pos.coords.longitude}`
      this.props.searchRoutes(null, false, formattedCoords)

      this.setState({
        geolocationLoading: false,
      })
    }, () => {
      this.setState({
        geolocationError: true,
        geolocationLoading: false,
      })
    }, { enableHighAccuracy: true })
  }

  handleNextPageClick = () => {
    this.props.getNextRoutes()
  }

  handlePrevPageClick = () => {
    this.props.getPrevRoutes()
  }

  render () {
    const {
      googleMapsLoaded,
      searchLoading,
      searchResults,
      searchResultsCount,
      unitPreference,
      selectedRouteId,
      noRoutesFound,
      searchCoords,
      mapMarkers,
      nextLink,
      prevLink,
      siteSlug,
      kmlLink,
      intl,
      displayLocation,
    } = this.props

    const {
      geolocationError,
      geolocationLoading } = this.state

    const distanceUnit = (unitPreference === 'mi') ? 'mile' : 'kilometer'
    const elevationUnit = (unitPreference === 'mi') ? 'foot' : 'meter'

    const helmetValues = {
      siteSlug: siteSlug,
      displayLocation: displayLocation,
    }

    return (
      <div className={styles.routeSearchPage}>

        <Helmet
          title={intl.formatMessage(messages.title, helmetValues)}
          meta={[
            {
              'name': 'description',
              'content': intl.formatMessage(messages.metaDescription, helmetValues),
            },
            {
              'name': 'keywords',
              'content': intl.formatMessage(messages.metaKeywords, helmetValues),
            },
          ]} />

        <RouteSearchTitle site={siteSlug} />
        <RouteSearchForm
          displayLocation={displayLocation}
          onSubmit={this.handleRouteSearchSubmit}
          onGetLocationSuggestions={this.handleGetLocationSuggestions}
          onSearchRoutes={this.handleRouteSearch} />

        {noRoutesFound ?
          <Row>
            <Col xs={12}>
              <div className={styles.noRoutesFound}>
                <FormattedMessage
                  id='routesearch.nonefound'
                  defaultMessage='No results found' />
              </div>
            </Col>
          </Row> : null}

        {geolocationError ?
          <Row>
            <Col xs={12}>
              <div className={styles.geolocationError}>
                <FormattedMessage
                  id='routesearch.geolocationerror'
                  defaultMessage='There was a problem getting the current location' />
              </div>
            </Col>
          </Row> : null}

        {googleMapsLoaded ?
          <div>
            <div className={styles.mapHeader}>
              <a onClick={this.handleSearchMapLocation} className={styles.mapHeaderLink}>
                <FormattedMessage
                  id='routesearch.maplocation'
                  defaultMessage='Search This Map Location' />
              </a>
              <a onClick={this.handleShowMyLocation} className={styles.mapHeaderLink}>
                <FormattedMessage
                  id='routesearch.showmylocation'
                  defaultMessage='Show My Location' />
              </a>
            </div>
            <RouteMap
              center={searchCoords}
              markers={mapMarkers}
              kmlLink={kmlLink}
              searchLoading={searchLoading || geolocationLoading}
              onMapLoad={this.handleMapLoad} />
          </div> : null}

        {searchResults.length ?
          <RouteSearchResults
            results={searchResults}
            totalCount={searchResultsCount}
            unitPreference={{ distance: distanceUnit, elevation: elevationUnit }}
            selectRouteAction={(routeId) => this.handleSelectRoute(routeId)}
            selectedRouteId={selectedRouteId}
            searchLoading={searchLoading || geolocationLoading}
            sortRoutesByDateAction={(showOldest) => this.handleSortRoutesByDate(showOldest)} /> : null}

        <div className={styles.paginateContainer}>
          {prevLink ?
            <a className={styles.pageLink} onClick={this.handlePrevPageClick}>
              <FormattedMessage id='routesearch.prevlink' defaultMessage='Previous' />
            </a> : null}

          {nextLink ?
            <a className={styles.pageLink} onClick={this.handleNextPageClick}>
              <FormattedMessage id='routesearch.nextlink' defaultMessage='Next' />
            </a> : null}
        </div>
      </div>
    )
  }
}

export const Page = injectIntl(RouteSearchPage)
export default connect(mapStateToProps, mapDispatchToActions)(Page)



// WEBPACK FOOTER //
// ./src/components/routes/search/RouteSearchPage.jsx